/**
 * A piece type's param form, built from its param specs (D22,
 * `lib/playout/paramForm.ts`): a string as a text field (with `enum`, a
 * picker), a number or integer the same (a numeric `enum` is a picker too), a
 * boolean as a checkbox, an object as a group of its own fields, and a list
 * as its items with add and remove — as deep as the spec goes. And the mode:
 * play now into the on-air part, or queue as the next part.
 *
 * The values are checked as the production will check them before anything
 * is sent; each error shows beside its field.
 */
import { PlusIcon, XIcon } from 'lucide-react';
import type { ParamSpec } from '@acp/engine/productionConfig.js';
import type { JsonValue } from '@acp/domain/primitives.js';
import { type FormEvent, useState } from 'react';
import { type Draft, emptyDraft, labelOf, mustFill, type ParamField, parseParams } from '../../lib/playout/paramForm.js';
import { Button } from '../ui/button.js';
import { Field, FieldError } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { NativeSelect } from '../ui/native-select.js';

export type AdlibParamFormProps = {
  readonly title: string;
  readonly fields: readonly ParamField[];
  readonly mode: 'now' | 'queue';
  readonly disabled: boolean;
  readonly onSubmit: (input: { readonly params: Record<string, JsonValue>; readonly mode: 'now' | 'queue' }) => void;
  readonly onCancel: () => void;
};

type ControlProps = {
  readonly path: string;
  readonly label: string;
  readonly spec: ParamSpec;
  readonly draft: Draft;
  readonly errors: Readonly<Record<string, string>>;
  readonly onChange: (draft: Draft) => void;
};

function idOf(path: string): string {
  return `adlib-param-${path}`;
}

/** The picker's options for an enum spec, as the text the draft holds; null: no enum (a text field). */
function optionsOf(spec: ParamSpec): readonly string[] | null {
  if (spec.type === 'string') return spec.enum;
  if (spec.type === 'number' || spec.type === 'integer') return spec.enum === undefined ? null : spec.enum.map(String);
  return null;
}

function TextControl({ path, label, spec, draft, errors, onChange }: ControlProps) {
  const id = idOf(path);
  const error = errors[path];
  const text = draft.kind === 'text' ? draft.text : '';
  const options = optionsOf(spec);
  return (
    <Field invalid={error !== undefined}>
      <Label htmlFor={id}>
        {label}
        {mustFill(spec) ? ' *' : ''}
      </Label>
      {options === null ? (
        <Input
          id={id}
          value={text}
          inputMode={spec.type === 'string' ? undefined : 'decimal'}
          onChange={(e) => onChange({ kind: 'text', text: e.target.value })}
          aria-invalid={error !== undefined}
          aria-describedby={`${id}-error`}
        />
      ) : (
        <NativeSelect id={id} value={text} onChange={(e) => onChange({ kind: 'text', text: e.target.value })} aria-invalid={error !== undefined} aria-describedby={`${id}-error`}>
          <option value="">–</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </NativeSelect>
      )}
      <FieldError id={`${id}-error`}>{error}</FieldError>
    </Field>
  );
}

function ObjectControl({ path, label, spec, draft, errors, onChange }: ControlProps) {
  if (spec.type !== 'object') return null;
  const fields = draft.kind === 'object' ? draft.fields : {};
  return (
    <fieldset className="grid gap-3 rounded-md border p-3" data-testid="adlib-param-group" data-path={path}>
      <legend className="px-1 text-sm font-medium">
        {label}
        {mustFill(spec) ? ' *' : ''}
      </legend>
      {Object.entries(spec.properties).map(([key, child]) => (
        <Control
          key={key}
          path={`${path}.${key}`}
          label={labelOf(key, child)}
          spec={child}
          draft={fields[key] ?? emptyDraft(child)}
          errors={errors}
          onChange={(next) => onChange({ kind: 'object', fields: { ...fields, [key]: next } })}
        />
      ))}
    </fieldset>
  );
}

function ArrayControl({ path, label, spec, draft, errors, onChange }: ControlProps) {
  if (spec.type !== 'array') return null;
  const items = draft.kind === 'array' ? draft.items : [];
  const full = spec.maxItems !== null && items.length >= spec.maxItems;
  const error = errors[path];
  return (
    <fieldset className="grid gap-2 rounded-md border p-3" data-testid="adlib-param-list" data-path={path} aria-describedby={`${idOf(path)}-error`}>
      <legend className="px-1 text-sm font-medium">
        {label}
        {mustFill(spec) ? ' *' : ''}
      </legend>
      {items.length === 0 ? <p className="text-sm text-muted-foreground">No items.</p> : null}
      {items.map((item, index) => {
        const itemLabel = `${label} ${String(index + 1)}`;
        return (
          // Items have no identity but their place: removing one re-keys those after it, which re-mounts them with their drafts.
          <div key={`${path}[${String(index)}]`} className="flex items-start gap-1.5" data-testid="adlib-param-item">
            <div className="min-w-0 flex-1">
              <Control
                path={`${path}[${String(index)}]`}
                label={itemLabel}
                spec={{ ...spec.items, required: true }}
                draft={item}
                errors={errors}
                onChange={(next) => onChange({ kind: 'array', items: items.map((d, i) => (i === index ? next : d)) })}
              />
            </div>
            <Button type="button" variant="ghost" size="sm" aria-label={`Remove ${itemLabel}`} onClick={() => onChange({ kind: 'array', items: items.filter((_, i) => i !== index) })}>
              <XIcon />
            </Button>
          </div>
        );
      })}
      <Button type="button" variant="outline" size="sm" className="w-fit" disabled={full} onClick={() => onChange({ kind: 'array', items: [...items, emptyDraft(spec.items)] })}>
        <PlusIcon />
        Add {label.toLowerCase()}
      </Button>
      <FieldError id={`${idOf(path)}-error`}>{error}</FieldError>
    </fieldset>
  );
}

function Control(props: ControlProps) {
  const { path, label, spec, draft, onChange } = props;
  if (spec.type === 'boolean') {
    const id = idOf(path);
    return (
      <label className="flex items-center gap-2 text-sm" htmlFor={id}>
        <input id={id} type="checkbox" checked={draft.kind === 'bool' && draft.value} onChange={(e) => onChange({ kind: 'bool', value: e.target.checked })} />
        {label}
      </label>
    );
  }
  if (spec.type === 'object') return <ObjectControl {...props} />;
  if (spec.type === 'array') return <ArrayControl {...props} />;
  return <TextControl {...props} />;
}

export function AdlibParamForm({ title, fields, mode, disabled, onSubmit, onCancel }: AdlibParamFormProps) {
  const [drafts, setDrafts] = useState<Readonly<Record<string, Draft>>>(() => Object.fromEntries(fields.map((f) => [f.name, f.initial])));
  const [chosenMode, setMode] = useState(mode);
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = parseParams(fields, drafts);
    if (!parsed.ok) {
      setErrors(parsed.errors);
      return;
    }
    setErrors({});
    onSubmit({ params: parsed.params, mode: chosenMode });
  };
  return (
    <form className="grid gap-3 rounded-md border bg-card p-3" onSubmit={submit} data-testid="adlib-param-form" aria-label={`Adlib ${title}`}>
      <p className="text-sm font-medium">{title}</p>
      {fields.map((field) => (
        <Control
          key={field.name}
          path={field.name}
          label={field.label}
          spec={field.spec}
          draft={drafts[field.name] ?? field.initial}
          errors={errors}
          onChange={(next) => setDrafts({ ...drafts, [field.name]: next })}
        />
      ))}
      <fieldset className="flex gap-4 text-sm">
        <legend className="sr-only">When</legend>
        <label className="flex items-center gap-1.5">
          <input type="radio" name="adlib-mode" checked={chosenMode === 'now'} onChange={() => setMode('now')} />
          Now
        </label>
        <label className="flex items-center gap-1.5">
          <input type="radio" name="adlib-mode" checked={chosenMode === 'queue'} onChange={() => setMode('queue')} />
          Queue as next
        </label>
      </fieldset>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={disabled} data-testid="adlib-submit">
          {chosenMode === 'now' ? 'Play now' : 'Queue'}
        </Button>
      </div>
    </form>
  );
}
